import { Image as ImageIcon, Loader2, Upload, X } from 'lucide-react';
import { useId, type ChangeEvent } from 'react';
import { cn } from '../lib/cn';

export interface LogoUploaderProps {
  /** Floating label rendered on the field's top edge. */
  label?: string;
  /**
   * Current logo URL to display in the thumbnail. `null` / empty string
   * shows the placeholder icon. Typically the SAS URL returned by the
   * organisation read endpoint.
   */
  value: string | null;
  /**
   * Called with the freshly picked `File` when the operator selects an
   * image. The parent is expected to stream it to the upload endpoint and
   * trigger a refetch (or optimistic patch) so a new `value` prop arrives.
   */
  onUpload: (file: File) => void;
  /**
   * Called when the operator clicks the clear (X) button. Only rendered
   * when there's a current value.
   */
  onClear?: () => void;
  /** Show a spinner instead of the upload prompt while a request is in-flight. */
  isUploading?: boolean;
  /** Show a thin border around the whole field, like TextInput. */
  bordered?: boolean;
  /** Disable interaction (read-only display). */
  disabled?: boolean;
  className?: string;
}

/**
 * Logo upload tile — preview thumbnail on the left, click-or-drag prompt on
 * the right. Lifted from My.Tms.Old/components/LogoUploader.tsx but rewired
 * to surface the picked `File` directly so the parent can stream it via the
 * dedicated upload endpoint (rather than round-tripping a data-URI through
 * the main organisation PUT body).
 */
export function LogoUploader({
  label,
  value,
  onUpload,
  onClear,
  isUploading = false,
  bordered = true,
  disabled = false,
  className,
}: LogoUploaderProps) {
  const reactId = useId();
  const hasLogo = !!value;
  const interactionDisabled = disabled || isUploading;

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file twice still fires the change event.
    e.target.value = '';
    if (!file) return;
    onUpload(file);
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="relative">
        <div
          className={cn(
            'flex min-h-[82px] items-center gap-3 rounded-sm bg-white px-2.5 py-2.5',
            bordered && 'border border-slate-300',
            interactionDisabled && 'pointer-events-none opacity-60',
          )}
        >
          <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-sm border border-slate-200 bg-slate-50">
            {hasLogo ? (
              <img src={value!} alt="Organisation logo" className="h-full w-full object-contain" />
            ) : (
              <ImageIcon className="h-8 w-8 text-slate-400" aria-hidden="true" />
            )}
          </div>
          <label htmlFor={reactId} className="flex flex-1 cursor-pointer flex-col gap-0.5 text-sm">
            {isUploading ? (
              <span className="flex items-center gap-1 font-medium text-blue-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Uploading…
              </span>
            ) : (
              <span className="flex items-center gap-1 font-medium text-blue-600">
                <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                Click or drag an image
              </span>
            )}
            <span className="text-xs text-slate-500">PNG, JPG, GIF, WEBP or SVG. Max 5 MB.</span>
            <input
              id={reactId}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              className="sr-only"
              onChange={handleFile}
              disabled={interactionDisabled}
            />
          </label>
          {hasLogo && !interactionDisabled && onClear && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Remove logo"
              className="flex h-6 w-6 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {label && (
          <span className="absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500">
            {label}
          </span>
        )}
      </div>
    </div>
  );
}
